import clsx from 'clsx';
import { NswContractStage } from '@urbanx/agx-ui-components';
import {
  CampaignDetailModel,
  CampaignDetailModelStage,
  CampaignDetailModelState,
  ContractCompletionType,
  ContractDetailModel,
  FormType,
  UploadedDocumentDocumentType,
} from '@urbanx/agx.campaigns.client';
import { AgxButton, AgxTooltip, useDialog } from '@urbanx/agx.ui-components2';
import { useFormLoader } from 'hooks/useFormLoader';
import ConvertExchangeMethodDialog from './ConvertExchangeMethodDialog';

interface ConvertExchangeMethodButtonProps {
  campaignId: string;
  contractId: string;
  destination: FormType.ContractExchange | FormType.ContractSubmission;
  hasExchangeLetter: boolean;
  blankContractIsLive: boolean;
  disabled: boolean;
  mobile: boolean;
  size?: 'medium' | 'large';
  variant?: 'primary' | 'hollow';
  stretch?: boolean;
}

export const showSolicitorExchangeConversion = (
  campaign: CampaignDetailModel,
  contract: ContractDetailModel
) => {
  if (campaign.state !== CampaignDetailModelState.NSW) return false;
  if (contract.contractCompletionType !== ContractCompletionType.ContractExchange) return false;
  if (
    campaign.stage !== CampaignDetailModelStage.ListingCurrent &&
    campaign.stage !== CampaignDetailModelStage.ListingUnderContract
  )
    return false;
  if (campaign.completionState?.completionType) return false;

  switch (contract.contractStage) {
    case NswContractStage.ContractDraft:
      return (
        contract.documents?.some(
          (document) => document.documentType === UploadedDocumentDocumentType.ContractExchange
        ) === true
      );
    case NswContractStage.BuyerSigning:
    case NswContractStage.VendorSigning:
    case NswContractStage.Signing:
    case NswContractStage.PendingAgentExchange:
    case NswContractStage.SubmissionIncomplete:
      return true;
    default:
      return false;
  }
};

const ConvertExchangeMethodButton = ({
  campaignId,
  contractId,
  destination,
  hasExchangeLetter,
  blankContractIsLive,
  disabled,
  mobile,
  size = 'large',
  variant = 'hollow',
  stretch = mobile,
}: ConvertExchangeMethodButtonProps) => {
  const toAgentExchange = destination === FormType.ContractExchange;
  const { loadFormWithConfig } = useFormLoader();
  const confirmation = useDialog(ConvertExchangeMethodDialog, { loadFormWithConfig });

  const unavailableReason = hasExchangeLetter ? 'No longer available' : undefined;

  return (
    <AgxTooltip
      id="exchange-conversion-unavailable"
      content={unavailableReason}
      disabled={!unavailableReason}
      triggerTabIndex={unavailableReason ? 0 : undefined}
      triggerAriaLabel={unavailableReason ? 'Why conversion is unavailable' : undefined}
      placement="top"
      className={clsx({ 'agx-button-tooltip-anchor--stretch': stretch })}
    >
      <AgxButton
        id={
          toAgentExchange
            ? 'convert-sales-advice-to-agent-exchange'
            : 'convert-agent-exchange-to-sales-advice'
        }
        text={toAgentExchange ? 'Convert to Agent Exchange' : 'Submit as Solicitor Exchange'}
        variant={variant}
        size={size}
        stretch={stretch}
        disabled={disabled || !!unavailableReason}
        onClick={() =>
          confirmation.open({ campaignId, contractId, destination, blankContractIsLive })
        }
      />
    </AgxTooltip>
  );
};

export default ConvertExchangeMethodButton;
